import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import {
  selectAll3DMeasurements,
  selectMeasurementToolSlice,
} from "@/store/measurement-tool-selector";
import {
  Measurement,
  MeasurementToolState,
  OnlyMeasurementToolStore,
} from "@/store/measurement-tool-slice";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { isIElementImg2d, isIElementImg360 } from "@faro-lotv/ielement-types";
import { IElementsState } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { FlyWalkSceneActiveElement } from "../flywalk-types";

/**
 *
 * @param main The current main element in flywalk mode
 * @returns The measurements relevant to the current flywalk scene
 */
export function useDispatchMeasurements(
  main: FlyWalkSceneActiveElement | null,
): Measurement[] {
  return useAppSelector(selectRelevantMeasurements(main));
}

const selectRelevantMeasurements = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectMeasurementToolSlice,
      (
        state: OnlyIElementsStore & OnlyMeasurementToolStore,
        main: FlyWalkSceneActiveElement | null,
      ) => main,
    ],
    computeRelevantMeasurements,
  ),
);

function computeRelevantMeasurements(
  iElements: IElementsState,
  measurementTool: MeasurementToolState,
  main: FlyWalkSceneActiveElement | null,
): Measurement[] {
  if (!main || isIElementImg2d(main) || isIElementOrthomosaic(main)) {
    return [];
  }

  const measurements = selectAll3DMeasurements({ iElements, measurementTool });

  if (isIElementImg360(main)) {
    return measurements;
  }

  return measurements.filter(
    (measurement) => !measurement.metadata.isEstimatedFrom2DPano,
  );
}
